import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { webOrigin } from '../utils/brand';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import PageShell from '../components/chrome/PageShell';
import LoadingSpinner from '../components/LoadingSpinner';
import { signalService, eventService } from '../services';
import messageService from '../services/messageService';
import ShareSheet from '../components/ShareSheet';
import { signalWindowLabel } from '../components/SignalCard';
import SignalText from '../components/SignalText';
import { useDialog } from '../context/DialogContext';
import { useAuth } from '../context/AuthContext';
import { usePortalPosting } from '../context/PortalPostingContext';
import PortalLogo from '../components/PortalLogo';
import ringArt from '../assets/img/portal-ring.png';
import { RING } from '../components/wall/wallTokens';
import { windowChip } from '../utils/portalLayout';
import useMediaQuery from '../utils/useMediaQuery';
import useTopBarHeight from '../hooks/useTopBarHeight';
import PortalCard from '../components/portal/page/PortalCard';
import PortalEditor from '../components/portal/page/PortalEditor';
import { openedLine } from '../utils/portalWords';
import {
  PortalPageStyles, RingBar, Backdrop, Btn, Box, SectionLabel, SquareAvatar,
  PinIcon, LockIcon, PencilIcon, CheckIcon, CYAN, MAGENTA, GREEN,
} from '../components/portal/page/PortalPageChrome';
import {
  windowLine, endsIn, firstName, roomsOf, roomLabel, backdropUrl, placeOf, mapsUrl,
  viewFor, exitFor, confirmFor, resultFor,
} from '../components/portal/page/portalPageModel';

/**
 * The Portal page — /portals/:id. The host manages a Portal here; a guest lands here after
 * I'M DOWN or VIEW PORTAL in the viewer. Design handoff: Portal Page (Sept 2026).
 *
 * It borrows the viewer's look, not the app's: the Portal's own clip or photo is the card on
 * the left and, blurred, the ground behind everything. Guests can leave a Portal they're in
 * and withdraw a pending ask; the host edits the Portal in place, in a viewer-style editor,
 * instead of the old white form.
 */

const MUTE_KEY = 'pr.storyMuted';

function expiresLabel(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const mins = Math.round(ms / 60000);
  return mins < 60 ? `expires in ${mins} min` : `expires in ${Math.round(mins / 60)} hr`;
}

/**
 * The moment you step through: the host's face in the spinning ring, their words, and what's
 * below. A curated Portal says you've asked and the host picks.
 */
function Arrival({ signal, onDismiss }) {
  const inside = signal.viewerState === 'accepted';
  return (
    <div
      className="pp-rise relative text-white"
      data-testid="portal-arrival"
      style={{ border: '2px solid transparent', background: `linear-gradient(#000,#000) padding-box, ${RING} border-box`, padding: '22px 20px' }}
    >
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="absolute font-mono" style={{ top: 10, right: 10, width: 28, height: 28, color: 'rgba(255,255,255,.7)', fontSize: 16 }}>×</button>
      )}
      <div className="flex items-center" style={{ gap: 16 }}>
        <span className="relative block flex-shrink-0" style={{ width: 64, height: 64 }} aria-hidden>
          <span className="pp-spin absolute inset-0 rounded-full" style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover' }} />
          <span className="absolute flex items-center justify-center overflow-hidden rounded-full bg-[#1A1A1A] font-mono font-bold" style={{ inset: '5.5%', fontSize: 20 }}>
            {signal.hostAvatarUrl
              ? <img src={signal.hostAvatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
              : (signal.hostDisplayName || '?').charAt(0).toUpperCase()}
          </span>
        </span>
        <div className="min-w-0">
          <p className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.2em', color: CYAN }}>
            {inside ? 'You’re in' : 'You asked to join'}
          </p>
          <p className="mt-1 italic" style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: 24, lineHeight: 1.15 }}>
            {signal.signalText ? `“${signal.signalText}”` : openedLine(signal.hostDisplayName)}
          </p>
          <p className="mt-2 font-mono" style={{ fontSize: 11, lineHeight: 1.5, color: 'rgba(255,255,255,0.7)' }}>
            {inside
              ? `${firstName(signal.hostDisplayName)}'s portal. Where it is and who's coming are below.`
              : `${firstName(signal.hostDisplayName)} picks who's in. You'll hear the moment they do.`}
          </p>
        </div>
      </div>
    </div>
  );
}

function Avatar({ url, name, size = 40 }) {
  return (
    <div className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-black bg-black font-mono text-xs font-bold text-white" style={{ width: size, height: size }}>
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : (name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

/** The two link tiles under every result: the square, and your own Portal. */
function OnwardTiles() {
  return (
    <>
      <Link to="/square" className="pp-tile block font-mono" style={{ padding: '14px 16px', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em' }}>
        ENTER THE CITY PORTAL →
        <span className="block" style={{ marginTop: 4, fontWeight: 400, letterSpacing: 0, opacity: 0.7 }}>See who’s out right now.</span>
      </Link>
      <Link to="/start" className="pp-tile block font-mono" style={{ padding: '14px 16px', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em' }}>
        OPEN YOUR OWN PORTAL
        <span className="block" style={{ marginTop: 4, fontWeight: 400, letterSpacing: 0, opacity: 0.7 }}>You pick who’s in.</span>
      </Link>
    </>
  );
}

function ResultBox({ title, body, children }) {
  return (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid="portal-result">
      <Box style={{ padding: 20 }}>
        <div className="font-mono font-bold uppercase" style={{ fontSize: 18, letterSpacing: '0.06em' }}>{title}</div>
        {body && <p className="font-mono" style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.6, color: 'rgba(255,255,255,.75)' }}>{body}</p>}
      </Box>
      {children}
    </div>
  );
}

export default function SignalDetailPage({ editor = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showConfirm } = useDialog();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { posting } = usePortalPosting();
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  // Arrived through I'M DOWN in the viewer: the page slides up, and there's a way back to the story.
  const fromStory = routerLocation.state?.fromStory || null;
  const mobile = useMediaQuery('(max-width: 699px)');
  const topBar = useTopBarHeight();
  const wantsJoin = searchParams.get('join') === '1'; // set on the post-signup redirect
  // Just stepped in — from I'M DOWN in the viewer (?joined=1), or I'M DOWN on this page.
  const [arrived, setArrived] = useState(() => searchParams.get('joined') === '1');
  const joinAttempted = useRef(false);
  const [signal, setSignal] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [responding, setResponding] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [decliningId, setDecliningId] = useState(null);
  const [declineNote, setDeclineNote] = useState('');
  // null | 'left' | 'withdrawn' | 'deleted' — replaces the action area; the card stays.
  const [exitResult, setExitResult] = useState(null);
  const [exiting, setExiting] = useState(false);
  const [editorOpen, setEditorOpen] = useState(editor);
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem(MUTE_KEY) !== 'false'; } catch { return true; } });
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    const r = await signalService.get(id);
    if (!r.success) {
      setError(r.error || 'Portal not found');
      setLoading(false);
      return;
    }
    setSignal(r.data);
    if (r.data.isMine) {
      const a = await signalService.listApplicants(id);
      if (a.success) setApplicants(a.data || []);
    } else if (r.data.viewerState === 'accepted') {
      const a = await signalService.listAttendees(id);
      if (a.success) setAttendees(a.data || []);
    } else {
      setAttendees([]);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  // The time-left line ticks once a minute.
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);
  // A replacement clip that just finished going up: pick up the real one.
  useEffect(() => { if (posting?.eventId === id && posting.status === 'up') load(); }, [posting?.status, posting?.eventId, id, load]);
  useEffect(() => { setEditorOpen(editor); }, [editor]);

  const review = async (applicantUserId, action, note) => {
    setBusyId(applicantUserId + action);
    const r = await signalService.reviewApplicant(id, applicantUserId, action, note);
    setBusyId(null);
    if (r.success) {
      setDecliningId(null);
      setDeclineNote('');
      setApplicants((prev) => prev.map((p) => (p.userId === applicantUserId ? { ...p, status: action === 'accept' ? 'accepted' : 'declined' } : p)));
      if (r.data) setSignal(r.data);
    }
  };

  const handleImIn = async () => {
    setResponding(true);
    const r = await signalService.imDown(id);
    setResponding(false);
    if (r.success) {
      setSignal(r.data);
      setExitResult(null);
      setArrived(true);
      load();
    }
  };

  // Arriving from a shared link after signing up (?join=1) joins in one tap.
  useEffect(() => {
    if (!isAuthenticated || !wantsJoin || !signal || joinAttempted.current) return;
    if (signal.isMine || signal.viewerState !== 'open') return;
    joinAttempted.current = true;
    handleImIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, wantsJoin, signal]);

  const viewProfile = (userId) => userId && navigate(`/users/${userId}`);

  const confirm = (kind, personName) => showConfirm({ ...confirmFor(kind, signal?.hostDisplayName, personName), variant: 'danger', theme: 'portal' });

  const kick = async (applicantUserId, name) => {
    if (!(await confirm('remove', name))) return;
    await review(applicantUserId, 'decline');
  };

  const exit = async (kind) => {
    if (!(await confirm(kind))) return;
    setExiting(true);
    if (kind === 'delete') {
      const r = await signalService.remove(id);
      setExiting(false);
      if (r.success) setExitResult('deleted');
      return;
    }
    const r = await signalService.withdraw(id);
    setExiting(false);
    if (r.success) {
      setExitResult(kind === 'leave' ? 'left' : 'withdrawn');
      setArrived(false);
      if (r.data) setSignal(r.data);
      load();
    }
  };

  const [openingGroup, setOpeningGroup] = useState(false);
  const openGroupChat = async () => {
    setOpeningGroup(true);
    const r = await messageService.openEventGroup(id);
    setOpeningGroup(false);
    if (r.success && r.data?.threadId) navigate(`/messages?thread=${r.data.threadId}`);
  };
  const messageHost = async () => {
    const r = await messageService.resolveSignalHostThread(signal.eventId);
    if (r.success && r.data?.threadId) navigate(`/messages?thread=${r.data.threadId}`);
  };

  const toggleMute = () => setMuted((m) => { const next = !m; try { localStorage.setItem(MUTE_KEY, String(next)); } catch { /* private mode */ } return next; });

  const closeEditor = () => {
    setEditorOpen(false);
    if (editor) navigate(`/portals/${id}`, { replace: true });
  };
  const onSaved = (card, mediaResult, opts) => {
    if (card) setSignal((s) => ({ ...s, ...card, media: mediaResult?.uploading ? null : (mediaResult?.changed ? mediaResult.media : s?.media) }));
    if (!opts?.deferClose) closeEditor();
    if (!mediaResult?.uploading) load();
  };

  // ---- derived ----
  const view = viewFor(signal);
  const host = firstName(signal?.hostDisplayName);
  const expired = !!signal?.expiresAtUtc && new Date(signal.expiresAtUtc).getTime() <= now;
  const live = !expired && !exitResult;
  // The server sends the place only to the host, the people who are in, and everyone once the
  // host has opened it (venue lock); here it is only ever drawn when it arrived.
  const revealed = live && (view === 'host' || !!signal?.venueName);
  const exitZone = live ? exitFor(view, signal?.hostDisplayName) : null;
  const result = exitResult ? resultFor(exitResult, signal?.hostDisplayName, signal?.expiresAtUtc) : null;
  const pending = applicants.filter((a) => a.status === 'pending');
  const inRoom = applicants.filter((a) => a.status === 'accepted' || a.status === 'paid');
  const place = placeOf(signal);
  const rooms = useMemo(() => roomsOf(signal || {}), [signal]);
  const chip = roomLabel(rooms);

  // Your own replacement clip while it's still going up: play the copy on this device.
  const localMedia = useMemo(() => (
    signal?.isMine && posting?.eventId === id && posting.localUrl && (posting.kind === 'video' || posting.kind === 'photo')
      ? { id: `local-${id}`, kind: posting.kind === 'photo' ? 'image' : 'video', mediaUrl: posting.localUrl, thumbnailUrl: posting.coverUrl || null, mediaStatus: 'ready', trimStartMs: posting.trim?.startMs ?? null, trimEndMs: posting.trim?.endMs ?? null, frame: posting.frame || null }
      : null
  ), [signal?.isMine, posting?.eventId, posting?.localUrl, posting?.kind, posting?.trim, posting?.frame, posting?.coverUrl, id]);
  const media = localMedia || (signal?.media && signal.media.mediaStatus !== 'failed' ? signal.media : null);
  const backdrop = backdropUrl(media);
  const timeChip = signal ? windowChip(signal.dateTimeUtc, signal.expiresAtUtc, now) : '';
  const venueOnCard = revealed && place
    ? { ...place, city: (signal?.primaryCityLabel || signal?.locationLabel || '').split(',')[0] }
    : (live && signal?.venueLocked ? { locked: true, city: (signal?.primaryCityLabel || signal?.locationLabel || '').split(',')[0] } : null);

  // Don't decide public-vs-member until auth resolves.
  if (authLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-white"><LoadingSpinner label="LOADING..." /></div>;
  }

  // Logged-out visitor (shared link): unchanged — the signal + a one-tap path to join by signing up.
  if (!isAuthenticated) {
    const authQuery = `?redirect=${encodeURIComponent(`/portals/${id}?join=1`)}`;
    const win = signal ? signalWindowLabel(signal.dateTimeUtc, signal.expiresAtUtc) : null;
    const exp = signal ? expiresLabel(signal.expiresAtUtc) : null;
    const isCurated = signal?.mode === 'curated';
    return (
      <div className="min-h-screen bg-white text-black">
        <header className="flex items-center border-b-2 border-black px-5 py-4 text-black">
          <PortalLogo cap={24} />
        </header>
        <main className="mx-auto max-w-md px-6 py-10">
          {loading ? (
            <div className="py-16 text-center"><LoadingSpinner label="LOADING..." /></div>
          ) : error ? (
            <div className="border-2 border-black bg-white p-8 text-center font-mono text-sm text-gray-500">{error}</div>
          ) : signal ? (
            <>
              <div className="border-2 border-black bg-white">
                <div className="flex items-center justify-between px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-black" style={{ background: '#00FFFF' }}>
                  <span>Portal</span>
                  <span>{isCurated ? 'Curated' : 'Open'}</span>
                </div>
                <div className="p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <Avatar url={signal.hostAvatarUrl} name={signal.hostDisplayName} size={32} />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold">{signal.hostDisplayName}</div>
                      <div className="font-mono text-[9px] uppercase tracking-wide text-gray-500">
                        Portal{win ? ` · ${win}` : (exp ? ` · ${exp}` : '')}
                      </div>
                    </div>
                  </div>
                  <h2 className="text-lg font-semibold leading-snug">{signal.signalText ? <>“<SignalText text={signal.signalText} />”</> : openedLine(signal.hostDisplayName)}</h2>
                </div>
              </div>
              {new Date(signal.expiresAtUtc).getTime() <= Date.now() ? (
                <div className="mt-5 border-2 border-black bg-gray-100 py-4 text-center font-mono text-xs font-bold uppercase tracking-widest text-gray-500">This portal has ended</div>
              ) : (
                <>
                  <p className="mb-4 mt-5 font-mono text-xs leading-relaxed text-gray-600">
                    <b className="text-black">{signal.hostDisplayName}</b> opened this portal.
                    {isCurated ? ' Apply and they’ll pick who’s in.' : ' Join and you’re in.'}
                  </p>
                  <button type="button" onClick={() => navigate(`/register${authQuery}`)} className="w-full bg-black py-3.5 text-center font-mono text-sm font-bold uppercase tracking-widest text-white hover:opacity-90">
                    {isCurated ? 'Apply with Portal' : 'Join with Portal'}
                  </button>
                  <button type="button" onClick={() => navigate(`/login${authQuery}`)} className="mt-3 w-full border-2 border-black bg-white py-3 text-center font-mono text-xs font-bold uppercase tracking-widest text-black hover:bg-black hover:text-white">
                    I already have an account
                  </button>
                </>
              )}
            </>
          ) : null}
        </main>
      </div>
    );
  }

  // ---- the action area for the current state ----
  const actions = (() => {
    if (!signal) return null;
    if (result) {
      return (
        <ResultBox title={result.title} body={result.body}>
          {result.rejoin && !expired && (
            <Btn kind="primary" size="lg" onClick={handleImIn} disabled={responding} style={{ letterSpacing: '0.1em' }} data-testid="rejoin">{responding ? '…' : result.rejoin}</Btn>
          )}
          <OnwardTiles />
        </ResultBox>
      );
    }
    if (view === 'host') {
      return (
        <>
          <div className="flex flex-col" style={{ gap: 10 }}>
            <Btn kind="primary" size="lg" onClick={openGroupChat} disabled={openingGroup}>{openingGroup ? 'Opening…' : 'Message attendees'}</Btn>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10 }}>
              <Btn kind="outline" onClick={() => setEditorOpen(true)} data-testid="edit-portal">Edit portal</Btn>
              <Btn kind="outline" onClick={() => setShareOpen(true)}>Invite people</Btn>
            </div>
          </div>

          <section className="flex flex-col" style={{ gap: 10 }}>
            <SectionLabel>Wants in · {pending.length}</SectionLabel>
            {pending.length === 0 ? (
              <Box style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,.7)' }}>No one waiting on your call right now.</Box>
            ) : pending.map((a) => (
              <Box key={a.userId}>
                <div className="flex flex-wrap items-center" style={{ gap: '10px 12px', padding: 10 }}>
                  <button type="button" onClick={() => viewProfile(a.userId)} className="flex min-w-0 items-center text-left" style={{ flex: '1 1 120px', gap: 12 }} title="View Portal">
                    <SquareAvatar url={a.avatarUrl} name={a.displayName} size={44} />
                    <span className="min-w-0">
                      <span className="block truncate" style={{ fontSize: 14, fontWeight: 700 }}>{a.displayName}</span>
                      {a.handle && <span className="block" style={{ fontSize: 11, color: 'rgba(255,255,255,.6)' }}>@{a.handle}</span>}
                    </span>
                  </button>
                  {decliningId !== a.userId && (
                    <div className="flex justify-end" style={{ gap: 8, flex: '1 0 auto' }}>
                      <Btn kind="outline" size="sm" style={{ flex: '1 0 auto' }} disabled={!!busyId} onClick={() => { setDecliningId(a.userId); setDeclineNote(''); }}>Not this one</Btn>
                      <Btn kind="green" size="sm" style={{ flex: '1 0 auto' }} disabled={!!busyId} onClick={() => review(a.userId, 'accept')}>Welcome in</Btn>
                    </div>
                  )}
                </div>
                {decliningId === a.userId && (
                  <div className="flex flex-col" style={{ borderTop: '2px dashed rgba(255,255,255,.35)', padding: 10, gap: 8 }}>
                    <input
                      type="text"
                      value={declineNote}
                      onChange={(e) => setDeclineNote(e.target.value)}
                      maxLength={500}
                      autoFocus
                      placeholder="Add a line if you want…"
                      className="w-full bg-transparent text-white"
                      style={{ border: '2px solid rgba(255,255,255,.5)', padding: '10px 12px', fontSize: 12, boxSizing: 'border-box' }}
                    />
                    <div className="flex" style={{ gap: 8 }}>
                      <Btn kind="primary" size="sm" style={{ flex: 1, padding: 10 }} disabled={!!busyId} onClick={() => review(a.userId, 'decline', declineNote)}>Send</Btn>
                      <Btn kind="outline" size="sm" style={{ flex: 1, padding: 10 }} disabled={!!busyId} onClick={() => review(a.userId, 'decline')}>Skip</Btn>
                    </div>
                  </div>
                )}
              </Box>
            ))}
          </section>

          <section className="flex flex-col" style={{ gap: 10 }}>
            <SectionLabel>In the room · {inRoom.length}</SectionLabel>
            <Box>
              {inRoom.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,.7)' }}>Nobody yet. Share it and they’ll come.</div>}
              {inRoom.map((a, i) => (
                <div key={a.userId} className="flex items-center" style={{ gap: 12, padding: 10, borderTop: i ? '2px solid rgba(255,255,255,.25)' : 0 }}>
                  <button type="button" onClick={() => viewProfile(a.userId)} className="flex min-w-0 flex-1 items-center text-left" style={{ gap: 12 }} title="View Portal">
                    <SquareAvatar url={a.avatarUrl} name={a.displayName} size={36} />
                    <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13 }}>{a.displayName}</span>
                  </button>
                  <span className="font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: GREEN }}>In</span>
                  <Btn kind="red" size="xs" disabled={!!busyId} onClick={() => kick(a.userId, a.displayName)}>Remove</Btn>
                </div>
              ))}
            </Box>
          </section>
        </>
      );
    }
    if (view === 'guest_in') {
      const me = user?.id;
      const others = attendees.filter((a) => a.userId !== me);
      const mine = attendees.find((a) => a.userId === me);
      const coming = mine ? [{ ...mine, displayName: `${firstName(mine.displayName)} (you)` }, ...others] : attendees;
      return (
        <>
          <div className="flex flex-col" style={{ gap: 10 }}>
            <div className="flex items-center justify-center font-bold uppercase" style={{ gap: 10, padding: 12, background: GREEN, color: '#000', fontSize: 12, letterSpacing: '0.16em' }} data-testid="youre-in">
              <CheckIcon /> You’re in
            </div>
            {signal.viewerTicketCode && (
              <Btn kind="primary" cyan size="lg" onClick={() => navigate(`/ticket/${signal.viewerTicketCode}`)}>Your pass to the portal →</Btn>
            )}
            <Btn kind="outline" onClick={messageHost}>Message {host}</Btn>
          </div>
          <section className="flex flex-col" style={{ gap: 10 }}>
            <SectionLabel>Who’s coming · {coming.length}</SectionLabel>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
              {coming.map((a) => (
                <button key={a.userId} type="button" onClick={() => viewProfile(a.userId)} className="pp-tile flex items-center text-left" style={{ gap: 10, padding: 8 }} title="View Portal">
                  <SquareAvatar url={a.avatarUrl} name={a.displayName} size={36} />
                  <span className="min-w-0 truncate" style={{ fontSize: 12, fontWeight: 700 }}>{a.displayName}</span>
                </button>
              ))}
            </div>
          </section>
        </>
      );
    }
    if (view === 'guest_asked') {
      return (
        <Box style={{ padding: 18 }} data-testid="asked-box">
          <div className="flex items-center font-bold uppercase" style={{ gap: 10, fontSize: 12, letterSpacing: '0.16em' }}>
            <span className="pp-pulse" style={{ width: 10, height: 10, background: MAGENTA }} aria-hidden />
            Asked to join · waiting on {host}
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.6, color: 'rgba(255,255,255,.75)' }}>{host} picks who’s in. You’ll hear the moment they do.</p>
        </Box>
      );
    }
    if (view === 'not_this_one') {
      return (
        <ResultBox title="Not this one." body={signal.viewerDeclineNote ? `“${signal.viewerDeclineNote}”` : 'Different Portal, different room. Try another.'}>
          <OnwardTiles />
        </ResultBox>
      );
    }
    if (expired) {
      return <ResultBox title="This portal has ended." body="The window has passed. No more joining." />;
    }
    const curated = signal.mode === 'curated';
    return (
      <div className="flex flex-col" style={{ gap: 10 }}>
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: 'rgba(255,255,255,.75)' }}>
          <strong style={{ color: '#fff' }}>{signal.hostDisplayName}</strong> opened this portal. {curated ? 'Ask and they pick who’s in.' : 'Join and you’re in.'}
        </p>
        <Btn kind="primary" size="lg" onClick={handleImIn} disabled={responding} style={{ fontSize: 14, letterSpacing: '0.08em' }} data-testid="im-down">{responding ? '…' : 'I’m down'}</Btn>
      </div>
    );
  })();

  const location = signal && (revealed && place ? (
    <Box className="flex items-stretch" data-testid="portal-location">
      <div className="flex flex-shrink-0 items-center justify-center" style={{ width: 56, borderRight: '2px solid #fff' }}><PinIcon /></div>
      <div className="min-w-0 flex-1" style={{ padding: '12px 14px' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{place.name}</div>
        {place.address && <div style={{ marginTop: 3, fontSize: 12, color: 'rgba(255,255,255,.7)' }}>{place.address}</div>}
      </div>
      {mapsUrl(signal) && (
        <a href={mapsUrl(signal)} target="_blank" rel="noopener noreferrer" className="pp-tile flex items-center" style={{ padding: '0 16px', border: 0, borderLeft: '2px solid #fff', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em' }}>MAPS →</a>
      )}
    </Box>
  ) : (
    <div className="flex items-center font-bold uppercase" style={{ gap: 12, padding: 14, border: '2px dashed rgba(255,255,255,.5)', fontSize: 11, letterSpacing: '0.12em', color: 'rgba(255,255,255,.75)' }} data-testid="portal-location-locked">
      <LockIcon /> {live ? 'Location shows once you’re in' : 'Location hidden'}
    </div>
  ));

  return (
    <PageShell bare>
      <div className="pp-page font-mono text-white" style={{ background: '#1A1A1A', animation: fromStory ? 'ppSlideUp .44s cubic-bezier(.2,.7,.2,1) both' : undefined }} data-testid="portal-page">
        <PortalPageStyles />
        <RingBar />
        <main className="relative overflow-hidden" style={{ minHeight: `calc(100vh - ${topBar + 4}px)` }}>
          <Backdrop url={backdrop} />

          <div className="relative" style={{ maxWidth: 1180, margin: '0 auto', padding: mobile ? '0 0 56px' : '24px 28px 72px', boxSizing: 'border-box' }}>
            {loading ? (
              <div className="py-16 text-center"><LoadingSpinner label="LOADING..." /></div>
            ) : error ? (
              <Box style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,.7)' }}>{error}</Box>
            ) : (
              <div className="flex flex-wrap items-start" style={{ gap: mobile ? 24 : 48 }}>
                {/* Left: the viewer card. */}
                <div className="flex flex-col" style={{ flex: mobile ? '1 1 100%' : '0 1 400px', minWidth: 260, position: mobile ? 'static' : 'sticky', top: topBar + 32, gap: 12 }}>
                  <PortalCard
                    signal={signal}
                    media={media}
                    mobile={mobile}
                    muted={muted}
                    onToggleMute={toggleMute}
                    roomLabel={chip}
                    chip={timeChip}
                    venue={venueOnCard}
                    opacity={exitResult === 'deleted' ? 0.4 : 1}
                  />
                  {!mobile && view === 'host' && live && (
                    <Btn kind="outline" className="flex w-full items-center justify-center" style={{ gap: 10, letterSpacing: '0.12em' }} onClick={() => setEditorOpen(true)}>
                      <PencilIcon /> Edit in viewer
                    </Btn>
                  )}
                </div>

                {/* Right: everything about it. On a phone it starts under the card. */}
                <div className="relative flex min-w-0 flex-col" style={{ flex: '1 1 420px', gap: 24, padding: mobile ? '24px 16px 0' : 0, zIndex: 1 }}>
                  {arrived && live && view !== 'host' && ['accepted', 'applied_pending'].includes(signal.viewerState) && (
                    <Arrival signal={signal} onDismiss={() => setArrived(false)} />
                  )}

                  <div className="flex flex-col" style={{ gap: 10 }}>
                    <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                      <span className="font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.2em', color: CYAN }}>
                        {view === 'host' ? 'Your portal' : `${host}’s portal`}
                      </span>
                      {result && <span className="font-bold uppercase" style={{ background: '#fff', color: '#000', padding: '2px 8px', fontSize: 10, letterSpacing: '0.1em' }} data-testid="ended-chip">{result.chip}</span>}
                    </div>
                    {/* Human content: never caps, so the DS's h1 rule is overridden here. */}
                    <h1 style={{ margin: 0, fontFamily: 'Arial, Helvetica, sans-serif', fontWeight: 400, fontSize: 'clamp(32px, 4.4vw, 52px)', lineHeight: 1.05, letterSpacing: '-0.01em', textTransform: 'none', overflowWrap: 'anywhere' }}>
                      {signal.signalText ? <SignalText text={signal.signalText} /> : openedLine(signal.hostDisplayName)}
                    </h1>
                    <div className="flex flex-wrap font-bold uppercase" style={{ gap: '6px 18px', fontSize: 11, letterSpacing: '0.1em', color: 'rgba(255,255,255,.75)' }}>
                      <span>{windowLine(signal.dateTimeUtc, signal.expiresAtUtc)}</span>
                      <span style={{ color: MAGENTA }}>{endsIn(signal.expiresAtUtc, now)}</span>
                    </div>
                    {signal.sourceEvent && (
                      <Link to={`/events/${signal.sourceEvent.inviteCode}`} className="pp-tile flex items-stretch" style={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
                        {signal.sourceEvent.posterUrl && <img src={signal.sourceEvent.posterUrl} alt="" className="h-12 w-12 flex-shrink-0 object-cover" style={{ borderRight: '2px solid currentColor' }} referrerPolicy="no-referrer" />}
                        <span className="flex min-w-0 flex-col justify-center" style={{ padding: '8px 12px' }}>
                          <span className="uppercase" style={{ fontSize: 9, letterSpacing: '0.12em', opacity: 0.7 }}>{signal.sourceEvent.isUpcoming ? 'The event' : 'The event · past'}</span>
                          <span className="truncate uppercase" style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em' }}>{signal.sourceEvent.title}</span>
                        </span>
                        <span className="flex flex-shrink-0 items-center" style={{ paddingRight: 12, fontSize: 13, fontWeight: 700 }}>→</span>
                      </Link>
                    )}
                  </div>

                  {location}

                  {actions}

                  {exitZone && (
                    <div className="flex flex-wrap items-center justify-between" style={{ marginTop: 12, paddingTop: 20, borderTop: '2px dashed rgba(255,255,255,.35)', gap: '12px 20px' }} data-testid="exit-zone">
                      <p style={{ margin: 0, flex: '1 1 220px', fontSize: 11, lineHeight: 1.6, color: 'rgba(255,255,255,.7)' }}>{exitZone.copy}</p>
                      <Btn kind="red" size="md" style={{ padding: '12px 18px', flexShrink: 0 }} disabled={exiting} onClick={() => exit(exitZone.kind)}>{exiting ? '…' : exitZone.label}</Btn>
                    </div>
                  )}

                  <Link to="/people" className="underline" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)' }}>← BACK TO PEOPLE</Link>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {fromStory && (
        <button
          type="button"
          onClick={() => navigate(fromStory)}
          data-testid="back-to-story"
          className="prc-pill fixed flex items-center font-mono font-bold uppercase"
          style={{ left: 12, bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', zIndex: 60, gap: 6, height: 36, padding: '0 14px 0 10px', background: '#000', color: '#fff', border: 0, borderRadius: 999, fontSize: 10, letterSpacing: '0.12em', boxShadow: '0 2px 10px rgba(0,0,0,.4)', cursor: 'pointer' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="square" strokeLinejoin="miter" aria-hidden><path d="M15 5l-7 7 7 7" /></svg>
          Back to the story
        </button>
      )}

      {signal && (
        <ShareSheet
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          title={signal.signalText || 'Your portal'}
          url={`${webOrigin()}/portals/${signal.eventId}`}
          inviteCode={signal.inviteCode}
          date={signal.dateTimeUtc}
          status="selling"
          mode={signal.mode}
          eventId={signal.eventId}
          eventService={eventService}
        />
      )}

      {editorOpen && signal?.isMine && (
        <PortalEditor signal={signal} media={signal.media || null} mobile={mobile} onClose={closeEditor} onSaved={onSaved} />
      )}
    </PageShell>
  );
}
